// iter146 — Sticky mobile app header. Hosts drawer toggle, title + role
// pill, bell, and (new) an admin-only school switcher: tapping the title
// row opens a full-height bottom sheet with search + the active-school
// list so admins can impersonate any school from a phone (previously
// only available in the desktop <Navbar>).
import { useMemo, useState } from "react";
import { Menu, Bell, Building2, Search, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useActiveSchool } from "@/lib/activeSchool";
import { useUnreadCount } from "@/lib/useUnreadCount";
import { useSheetBackButton } from "@/lib/useSheetBackButton";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/components/ui/drawer";

// iter150-review — Mobile AppHeader bell unread dot. Pulls from the
// shared useUnreadCount() hook so the AppHeader badge, the BottomNav
// Inbox badge and the desktop InboxBell always agree. Role filtering
// happens in the shared hook — admin sees admin counts, schools see
// school counts; the two are NEVER summed.
function HeaderUnreadDot() {
  const count = useUnreadCount();
  if (count <= 0) return null;
  return (
    <span
      data-testid="app-header-bell-badge"
      aria-label={`${count} unread messages`}
      className="absolute -top-0.5 -right-0.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-rose-500 px-1 text-[0.58rem] font-bold text-white shadow-sm"
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

function AdminSchoolSwitcherSheet({ open, onClose }) {
  const { profiles, activeId, setActiveId } = useActiveSchool();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  // iter150-review — Android/browser Back button closes the sheet
  // instead of leaving /admin. Picking a school replaces the sheet
  // marker in history so one press of back returns to /admin.
  useSheetBackButton(open, (v) => { if (!v) onClose(); });
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = profiles || [];
    if (!needle) return list.slice(0, 50);
    return list
      .filter((p) => {
        const name = (p.schoolName || "").toLowerCase();
        const udise = (p.udiseCode || "").toLowerCase();
        return name.includes(needle) || udise.includes(needle);
      })
      .slice(0, 50);
  }, [profiles, q]);
  const pick = (p) => {
    setActiveId(p.id);
    onClose();
    // Give sheet cleanup a tick to pop its history marker before we
    // push the destination — guarantees Back from /setup returns to
    // /admin (not back to the sheet).
    setTimeout(() => navigate("/setup"), 0);
  };
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        data-testid="admin-school-switcher-sheet"
        className="max-h-[88vh] rounded-t-3xl border-t border-slate-200 bg-white"
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-base font-semibold text-slate-900">Switch school</DrawerTitle>
          <DrawerDescription className="text-xs text-slate-600">
            Pick a school to act on its behalf. Loads its Setup page.
          </DrawerDescription>
        </DrawerHeader>
        <div className="flex items-center gap-2 border-y border-slate-100 bg-slate-50 px-4 py-2">
          <Search className="h-4 w-4 text-slate-500" />
          <input
            type="text"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by UDISE or school name"
            data-testid="admin-school-switcher-input"
            className="w-full bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400"
          />
        </div>
        <ul
          className="max-h-[55vh] overflow-y-auto"
          data-testid="admin-school-switcher-list"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-10 text-center text-sm text-slate-500">
              No schools match "{q}".
            </li>
          )}
          {filtered.map((p) => {
            const isActive = p.id === activeId;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => pick(p)}
                  data-testid={`admin-school-switcher-item-${p.udiseCode}`}
                  className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors active:bg-slate-100 ${
                    isActive ? "bg-brand-50" : "bg-white"
                  }`}
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-600">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{p.schoolName || "—"}</p>
                    <p className="truncate font-mono text-xs text-slate-600">UDISE {p.udiseCode}</p>
                  </div>
                  {isActive && <Check className="h-5 w-5 text-brand-600" aria-label="Active" />}
                </button>
              </li>
            );
          })}
        </ul>
        <DrawerClose asChild>
          <button
            type="button"
            data-testid="admin-school-switcher-close"
            className="mx-4 my-3 rounded-xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 active:bg-slate-200"
          >
            Close
          </button>
        </DrawerClose>
      </DrawerContent>
    </Drawer>
  );
}

export default function AppHeader({ onOpenDrawer, title }) {
  const { user } = useAuth();
  const { active } = useActiveSchool();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const subtitle = isAdmin
    ? (active?.schoolName ? `Acting as: ${active.schoolName}` : "No school selected")
    : (user.schoolName || "");
  return (
    <>
      <header
        data-testid="app-header"
        className="fixed inset-x-0 top-0 z-[90] flex h-14 items-center justify-between border-b border-slate-200 bg-white/85 px-3 backdrop-blur-xl md:hidden"
      >
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open menu"
          data-testid="app-header-drawer-toggle"
          className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-slate-100 active:scale-95"
        >
          <Menu className="h-5 w-5" strokeWidth={2.25} />
        </button>

        {isAdmin ? (
          <button
            type="button"
            onClick={() => setSwitcherOpen(true)}
            data-testid="app-header-admin-school-trigger"
            aria-label="Switch school"
            className="flex min-w-0 flex-1 items-center gap-2 px-2 text-left active:scale-[0.99]"
          >
            <img src="/logo192.png" alt="MDM Sahayak" className="h-7 w-7 rounded-lg shadow-sm" />
            <div className="flex min-w-0 flex-col leading-none">
              <span data-testid="app-header-title" className="truncate text-sm font-bold tracking-tight text-slate-900">
                {title || "MDM Sahayak"}
              </span>
              <span
                data-testid="app-header-subtitle"
                className="mt-0.5 inline-flex max-w-full truncate text-[11px] font-medium text-slate-600"
                title={subtitle}
              >
                {subtitle}
              </span>
            </div>
          </button>
        ) : (
          <Link to="/dashboard" className="flex min-w-0 flex-1 items-center gap-2 px-2">
            <img src="/logo192.png" alt="MDM Sahayak" className="h-7 w-7 rounded-lg shadow-sm" />
            <div className="flex min-w-0 flex-col leading-none">
              <span data-testid="app-header-title" className="truncate text-sm font-bold tracking-tight text-slate-900">
                {title || "MDM Sahayak"}
              </span>
              {subtitle && (
                <span
                  data-testid="app-header-subtitle"
                  className="mt-0.5 inline-flex max-w-full truncate text-[11px] font-medium text-slate-600"
                  title={subtitle}
                >
                  {subtitle}
                </span>
              )}
            </div>
          </Link>
        )}

        <Link
          to="/inbox"
          aria-label="Open inbox"
          data-testid="app-header-action"
          className="relative flex h-10 w-10 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-slate-100 active:scale-95"
        >
          <Bell className="h-5 w-5" strokeWidth={2.25} />
          <HeaderUnreadDot />
        </Link>
      </header>
      {isAdmin && (
        <AdminSchoolSwitcherSheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
      )}
    </>
  );
}
